/**
 * SlideLibrary: the org's reusable custom slides (Build → Slides).
 *
 * A host makes image, announcement, schedule and promo slides here, or
 * copies a built-in starter. The Game Night hub then places a slide into
 * a night's presentation.
 */
import { useCallback, useEffect, useMemo, useState, type ChangeEvent, type FormEvent } from 'react';
import { toast } from 'react-toastify';
import {
  AlertDialog,
  Badge,
  Box,
  Button,
  Card,
  Dialog,
  Flex,
  Grid,
  Heading,
  Select,
  Spinner,
  Text,
  TextArea,
  TextField,
} from '@radix-ui/themes';
import api from '../../utils/api';
import {
  KIND_HELP,
  KIND_LABELS,
  fetchSlideTemplates,
  fetchStarterTemplates,
  uploadSlideImage,
  type SlideTemplate,
  type StarterTemplate,
} from '../../utils/customSlides';
import type { CustomSlideKind } from '../../utils/slideSequence';
import SlidePreview, { templateAsSlide } from './SlidePreview';
import VenueDefaultsCard from './VenueDefaultsCard';

interface FormState {
  name: string;
  kind: CustomSlideKind;
  title: string;
  body: string;
  imageUrl: string;
  url: string;
  caption: string;
}

const EMPTY_FORM: FormState = {
  name: '', kind: 'TEXT', title: '', body: '', imageUrl: '', url: '', caption: '',
};

const KINDS: CustomSlideKind[] = ['TEXT', 'IMAGE', 'SCHEDULE', 'PROMO'];

function errorMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { message?: string } } };
  return e.response?.data?.message || fallback;
}

const SlideLibrary: React.FC = () => {
  const [templates, setTemplates] = useState<SlideTemplate[]>([]);
  const [starters, setStarters] = useState<StarterTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<SlideTemplate | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SlideTemplate | null>(null);
  const [copying, setCopying] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [t, s] = await Promise.all([fetchSlideTemplates(), fetchStarterTemplates()]);
      setTemplates(t);
      setStarters(s);
    } catch {
      toast.error('Failed to load slides');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copiedStarterKeys = useMemo(
    () => new Set(templates.map((t) => t.starterKey).filter(Boolean)),
    [templates],
  );

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEdit = (t: SlideTemplate) => {
    setEditing(t);
    setForm({
      name: t.name,
      kind: t.kind,
      title: t.title ?? '',
      body: t.body ?? '',
      imageUrl: t.imageUrl ?? '',
      url: t.url ?? '',
      caption: t.caption ?? '',
    });
    setFormOpen(true);
  };

  const onField = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onImage = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Only image files are allowed');
      return;
    }
    setUploading(true);
    try {
      const url = await uploadSlideImage(file);
      setForm((f) => ({ ...f, imageUrl: url }));
    } catch (err) {
      toast.error(errorMessage(err, 'Upload failed'));
    } finally {
      setUploading(false);
    }
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    // Send every field. The server stores a blank field as null.
    const body = { ...form };
    try {
      if (editing) {
        await api.put(`/api/slides/templates/${editing.id}`, body);
        toast.success('Slide saved');
      } else {
        await api.post('/api/slides/templates', body);
        toast.success('Slide added to your library');
      }
      setFormOpen(false);
      await load();
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to save slide'));
    } finally {
      setSaving(false);
    }
  };

  const copyStarter = async (key: string) => {
    setCopying(key);
    try {
      await api.post(`/api/slides/starters/${key}/copy`);
      toast.success('Starter added to your library');
      await load();
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to add starter'));
    } finally {
      setCopying(null);
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/api/slides/templates/${deleteTarget.id}`);
      toast.success('Slide deleted');
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to delete slide'));
    }
  };

  const previewSlide = templateAsSlide({
    kind: form.kind,
    title: form.title.trim() || null,
    body: form.body.trim() || null,
    imageUrl: form.imageUrl.trim() || null,
    url: form.url.trim() || null,
    caption: form.caption.trim() || null,
  });

  return (
    <Flex direction="column" gap="5">
      <Flex justify="between" align="start" gap="3" wrap="wrap">
        <Box>
          <Heading size="6">Slides</Heading>
          <Text size="2" color="gray">
            Reusable slides for your presentation. Place them into a game night from its hub.
          </Text>
        </Box>
        <Button onClick={openCreate}>New slide</Button>
      </Flex>

      {loading ? (
        <Card>
          <Flex align="center" gap="2">
            <Spinner />
            <Text color="gray">Loading slides…</Text>
          </Flex>
        </Card>
      ) : (
        <>
          <Flex direction="column" gap="3">
            <Heading size="3">Your library</Heading>
            {templates.length === 0 ? (
              <Card>
                <Flex direction="column" align="center" gap="2" py="5">
                  <Text color="gray">No slides yet</Text>
                  <Text size="2" color="gray" align="center" style={{ maxWidth: 480 }}>
                    Start from one of the starters below, or make a new slide.
                  </Text>
                </Flex>
              </Card>
            ) : (
              <Grid columns={{ initial: '1', sm: '2', md: '3' }} gap="4">
                {templates.map((t) => (
                  <Card key={t.id}>
                    <Flex direction="column" gap="3" height="100%">
                      <SlidePreview slide={templateAsSlide(t)} />
                      <Flex direction="column" gap="1">
                        <Text weight="bold">{t.name}</Text>
                        <Flex gap="2" align="center" wrap="wrap">
                          <Badge variant="soft">{KIND_LABELS[t.kind]}</Badge>
                          <Text size="1" color="gray">
                            {t._count?.placements
                              ? `Placed in ${t._count.placements} game night${t._count.placements > 1 ? 's' : ''}`
                              : 'Not placed yet'}
                          </Text>
                        </Flex>
                      </Flex>
                      <Flex gap="2" justify="end" mt="auto">
                        <Button size="1" variant="soft" onClick={() => openEdit(t)}>Edit</Button>
                        <Button size="1" variant="soft" color="red" onClick={() => setDeleteTarget(t)}>
                          Delete
                        </Button>
                      </Flex>
                    </Flex>
                  </Card>
                ))}
              </Grid>
            )}
          </Flex>

          <VenueDefaultsCard templates={templates} />

          <Flex direction="column" gap="3">
            <Box>
              <Heading size="3">Starters</Heading>
              <Text size="2" color="gray">
                Ready-made slides. Add one to your library, then edit it to fit your nights.
              </Text>
            </Box>
            <Grid columns={{ initial: '1', sm: '2', md: '4' }} gap="4">
              {starters.map((s) => {
                const added = copiedStarterKeys.has(s.key);
                return (
                  <Card key={s.key}>
                    <Flex direction="column" gap="2" height="100%">
                      <SlidePreview
                        slide={templateAsSlide({
                          kind: s.kind, title: s.title, body: s.body, imageUrl: null,
                          url: s.url ?? null, caption: s.caption ?? null,
                        })}
                      />
                      <Text weight="bold" size="2">{s.name}</Text>
                      <Text size="1" color="gray">{KIND_HELP[s.kind]}</Text>
                      <Flex justify="end" mt="auto">
                        <Button
                          size="1"
                          variant={added ? 'outline' : 'soft'}
                          disabled={copying === s.key}
                          onClick={() => copyStarter(s.key)}
                        >
                          {added ? 'Add another copy' : 'Add to library'}
                        </Button>
                      </Flex>
                    </Flex>
                  </Card>
                );
              })}
            </Grid>
          </Flex>
        </>
      )}

      {/* Create / edit dialog with a live preview */}
      <Dialog.Root open={formOpen} onOpenChange={(open) => !open && setFormOpen(false)}>
        <Dialog.Content maxWidth="920px">
          <Dialog.Title>{editing ? 'Edit slide' : 'New slide'}</Dialog.Title>
          <Dialog.Description size="2" color="gray" mb="4">
            {KIND_HELP[form.kind]}
          </Dialog.Description>
          <form onSubmit={save}>
            <Grid columns={{ initial: '1', md: '2' }} gap="5">
              <Flex direction="column" gap="3">
                <label>
                  <Text as="div" size="2" weight="medium" mb="1">Name</Text>
                  <TextField.Root
                    name="name"
                    value={form.name}
                    onChange={onField}
                    placeholder="e.g. Sponsor: Palisade Brewing"
                    required
                  />
                  <Text as="div" size="1" color="gray" mt="1">Only you see this name.</Text>
                </label>
                <Box>
                  <Text as="div" size="2" weight="medium" mb="1">Kind</Text>
                  <Select.Root
                    value={form.kind}
                    onValueChange={(v) => setForm((f) => ({ ...f, kind: v as CustomSlideKind }))}
                  >
                    <Select.Trigger aria-label="Slide kind" style={{ width: '100%' }} />
                    <Select.Content>
                      {KINDS.map((k) => (
                        <Select.Item key={k} value={k}>{KIND_LABELS[k]}</Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Root>
                </Box>
                <label>
                  <Text as="div" size="2" weight="medium" mb="1">Title</Text>
                  <TextField.Root
                    name="title"
                    value={form.title}
                    onChange={onField}
                    placeholder={form.kind === 'SCHEDULE' ? 'Next week' : 'Headline on the slide'}
                  />
                </label>
                {form.kind !== 'IMAGE' && (
                  <label>
                    <Text as="div" size="2" weight="medium" mb="1">
                      {form.kind === 'SCHEDULE' ? 'Footer line' : 'Message'}
                    </Text>
                    <TextArea name="body" rows={3} value={form.body} onChange={onField} />
                    <Text as="div" size="1" color="gray" mt="1">Each line shows as its own line.</Text>
                  </label>
                )}
                {form.kind === 'IMAGE' && (
                  <Box>
                    <Text as="div" size="2" weight="medium" mb="1">Image</Text>
                    <input type="file" accept="image/*" onChange={onImage} disabled={uploading} />
                    {uploading && <Text as="div" size="1" color="gray" mt="1">Uploading…</Text>}
                    <TextField.Root
                      mt="2"
                      name="imageUrl"
                      value={form.imageUrl}
                      onChange={onField}
                      placeholder="or paste an image URL"
                    />
                  </Box>
                )}
                {form.kind === 'PROMO' && (
                  <label>
                    <Text as="div" size="2" weight="medium" mb="1">Link for the QR code</Text>
                    <TextField.Root
                      type="url"
                      name="url"
                      value={form.url}
                      onChange={onField}
                      placeholder="https://"
                    />
                  </label>
                )}
                {(form.kind === 'IMAGE' || form.kind === 'PROMO') && (
                  <label>
                    <Text as="div" size="2" weight="medium" mb="1">Caption</Text>
                    <TextField.Root
                      name="caption"
                      value={form.caption}
                      onChange={onField}
                      placeholder={form.kind === 'PROMO' ? 'Scan to book' : 'Thanks to our sponsor'}
                    />
                  </label>
                )}
              </Flex>
              <Flex direction="column" gap="2">
                <Text size="2" weight="medium">Preview</Text>
                <SlidePreview slide={previewSlide} />
                {form.kind === 'SCHEDULE' && (
                  <Text size="1" color="gray">
                    On game night this lists the next entries on your public schedule for that venue.
                  </Text>
                )}
              </Flex>
            </Grid>
            <Flex gap="3" mt="5" justify="end">
              <Dialog.Close>
                <Button type="button" variant="soft" color="gray">Cancel</Button>
              </Dialog.Close>
              <Button type="submit" disabled={saving || uploading}>
                {saving ? 'Saving…' : editing ? 'Save slide' : 'Add slide'}
              </Button>
            </Flex>
          </form>
        </Dialog.Content>
      </Dialog.Root>

      {/* Delete confirmation */}
      <AlertDialog.Root open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialog.Content maxWidth="440px">
          <AlertDialog.Title>Delete slide</AlertDialog.Title>
          <AlertDialog.Description size="2">
            Delete &quot;{deleteTarget?.name}&quot;?
            {deleteTarget?._count?.placements
              ? ` It also comes out of ${deleteTarget._count.placements} game night${deleteTarget._count.placements > 1 ? 's' : ''}.`
              : ''}
          </AlertDialog.Description>
          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <Button variant="soft" color="gray">Cancel</Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button color="red" onClick={remove}>Delete</Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </Flex>
  );
};

export default SlideLibrary;
